import {
  ArrowBendUpRightIcon,
  ArrowUUpLeftIcon,
  CopySimpleIcon,
  DotsThreeIcon,
  PencilSimpleIcon,
  SparkleIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import type React from "react";
import { Fragment, useEffect, useRef, useState } from "react";
import PlaceHolderImage from "@/assets/images/article-placeholder.webp?url";
import Card from "@/components/ui/card";
import { Separator } from "@/components/ui/controls/separator";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import { Label } from "@/components/ui/field";
import IconButton from "@/components/ui/icon-button";
import {
  Menu,
  MenuPopover,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu";
import { CustomMenuItem, type MenuItemConfig } from "@/components/ui/menu-item";
import { toast } from "@/components/ui/toast";
import type { FeedArticleProjectionData } from "@/data-access/shared/fragments/feed-article-projection-fragments";
import { useFeature } from "@/lib/feature-management";
import { cn } from "@/lib/utils";
import { copyTextToClipboard } from "@/lib/utils/clipboard";
import { getPrettyName } from "@/lib/utils/prettyName";
import ArticleAuthor from "./article-author";
import { ArticleBadgeRow } from "./article-badge-row";
import ArticleHeader from "./article-header";
import ArticleImage from "./article-image";
import ArticleMentionText from "./article-mention-text";
import ArticleSummary from "./article-summary";
import { ArticleTagRow } from "./article-tag-row";
import ScoreDetail from "./score-detail";
import ScoreDisplay from "./score-display";
import { hasSecondSection } from "./utils";

const getTranslationLabel = (language?: string) => {
  if (
    !language ||
    typeof language !== "string" ||
    language === "ENGLISH" ||
    language === "UNKNOWN"
  ) {
    return null;
  }
  return (
    <Label className="text-xs flex flex-row gap-1 text-gray-400 font-thin italic">
      <SparkleIcon className="ml-1" weight="fill" size={12} />
      {`Translated from ${getPrettyName(language)}`}
    </Label>
  );
};

export interface ArticleCardProps {
  article: FeedArticleProjectionData;
  handleDeleteClick?: () => void;
  handleEditClick?: () => void;
  handleMoveToClusterClick?: () => void;
  handleRemoveFromClusterClick?: () => void;
  image?: HTMLImageElement;
  isPreview?: boolean;
  isLoading?: boolean;
  isSelected?: boolean;
}

/**
 * Article card component that displays article information including image, headline,
 * publisher, sentiment, impact score, and other metadata.
 *
 * This component supports interactive features like editing, selection, and keyboard navigation.
 */
const ArticleCard = ({
  article,
  handleDeleteClick,
  handleEditClick,
  handleMoveToClusterClick,
  handleRemoveFromClusterClick,
  image,
  isPreview = false,
  isLoading = false,
  isSelected = false,
}: ArticleCardProps) => {
  // All hooks must be called before any conditional returns
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isCardFocused, setIsCardFocused] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const enableArticleTagging = useFeature("enable-article-tagging", false);
  const canCopyFeedArticleId = useFeature("copy-feed-article-id", false);

  // Apply tabindex to internal elements based on card focus state
  useEffect(() => {
    if (!cardRef.current) return;
    // Find all interactive elements
    const elements = cardRef.current.querySelectorAll(
      'button, a, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    // Set tabindex based on card focus state
    for (const el of elements) {
      if (el instanceof HTMLElement) {
        el.tabIndex = isCardFocused ? 0 : -1;
      }
    }
  }, [isCardFocused]);

  // Set initial tabindex values on mount
  useEffect(() => {
    if (!cardRef.current) return;
    // Find all interactive elements
    const elements = cardRef.current.querySelectorAll(
      'button, a, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    // Set initial tabindex to -1 to prevent tab navigation
    for (const el of elements) {
      if (el instanceof HTMLElement) {
        el.tabIndex = -1;
      }
    }
  }, []);

  // Early return checks after all hooks
  if (!article.id) return null;

  const gridTemplateRows = "grid-rows-[auto]";

  const cardLayout =
    "p-5 text-left md:max-w-[900px] w-full relative grid grid-cols-[auto_1fr_1px_119px] gap-3.5 pb-2 overflow-visible";
  const centerSectionHeader = "flex min-w-0 flex-col gap-y-2";

  const copyFeedArticleId = async () => {
    const copied = await copyTextToClipboard(String(article.feedArticleId));
    if (copied) {
      toast.success("Feed Article ID copied");
    } else {
      toast.error("Could not copy Feed Article ID");
    }
  };

  const menuItems: (MenuItemConfig & { id: string })[] = [
    {
      id: "edit-article",
      label: (
        <span className="font-medium text-xs leading-4 tracking-[0.01em] text-gray-900">
          Edit article
        </span>
      ),
      icon: <PencilSimpleIcon size={14} className="me-2 text-gray-700" />,
      onClick: () => {
        handleEditClick?.();
      },
    },
    {
      id: "move-to-cluster",
      label: (
        <span className="font-medium text-xs leading-4 tracking-[0.01em] text-gray-900">
          Move to cluster
        </span>
      ),
      icon: <ArrowBendUpRightIcon size={14} className="me-2 text-gray-700" />,
      onClick: () => {
        handleMoveToClusterClick?.();
      },
    },
    ...(article.storyId
      ? [
          {
            id: "remove-from-cluster",
            label: (
              <span className="font-medium text-xs leading-4 tracking-[0.01em] text-gray-900">
                Remove from cluster
              </span>
            ),
            icon: <ArrowUUpLeftIcon size={14} className="me-2 text-gray-700" />,
            onClick: () => {
              handleRemoveFromClusterClick?.();
            },
          },
        ]
      : []),
    {
      id: "copy-feed-article-id",
      label: (
        <span className="font-medium text-xs leading-4 tracking-[0.01em] text-gray-900">
          Copy Feed Article ID
        </span>
      ),
      icon: <CopySimpleIcon size={14} className="me-2 text-gray-700" />,
      onClick: () => void copyFeedArticleId(),
      condition: canCopyFeedArticleId && Boolean(article.feedArticleId),
    },
    {
      id: "delete-article",
      label: (
        <span className="font-medium text-xs leading-4 tracking-[0.01em] text-gray-900">
          Delete article
        </span>
      ),
      icon: <TrashIcon size={14} className="me-2 text-red-750" />,
      onClick: () => {
        handleDeleteClick?.();
      },
    },
  ];

  // Handle keyboard navigation within the card
  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    // When in card navigation mode, trap focus within the card
    if (
      isCardFocused &&
      e.key === "Tab" &&
      cardRef.current?.contains(document.activeElement)
    ) {
      // Find all focusable elements
      const focusableElements = Array.from(
        cardRef.current.querySelectorAll(
          'button, a, [href], input, select, textarea, [tabindex="0"]',
        ),
      ).filter((el) => el instanceof HTMLElement) as HTMLElement[];

      if (focusableElements.length === 0) return;

      const focusedElement = document.activeElement;
      if (
        !focusedElement ||
        !cardRef.current.contains(focusedElement) ||
        !(focusedElement instanceof HTMLElement)
      ) {
        return;
      }

      const currentIndex = focusableElements.indexOf(focusedElement);

      // Calculate the next index based on shift key
      let nextIndex: number;
      if (e.shiftKey) {
        // Go to previous element, or wrap to the end
        nextIndex =
          currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
      } else {
        // Go to next element, or wrap to the beginning
        nextIndex =
          currentIndex === focusableElements.length - 1 ? 0 : currentIndex + 1;
      }

      // Prevent default tab behavior
      e.preventDefault();

      // Focus the next element if it exists
      if (nextIndex >= 0 && nextIndex < focusableElements.length) {
        focusableElements[nextIndex]?.focus();
      }
      return;
    }

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault(); // Prevent scrolling on space

      if (!isCardFocused) {
        // Enter the card's internal navigation
        setIsCardFocused(true);

        // Focus the first interactive element inside the card
        setTimeout(() => {
          if (cardRef.current) {
            // Find all focusable elements
            const focusableElements = cardRef.current.querySelectorAll(
              'button, a, [href], input, select, textarea, [tabindex="0"]',
            );

            // Focus the first one if it exists
            if (focusableElements.length > 0) {
              (focusableElements[0] as HTMLElement).focus();
            }
          }
        }, 0);
      }
    } else if (e.key === "Escape" && isCardFocused) {
      e.preventDefault(); // Prevent any default escape behavior
      setIsCardFocused(false);

      // Return focus to the card
      setTimeout(() => {
        cardRef.current?.focus();
      }, 0);
    }
  };

  if (isLoading) {
    return (
      <Card className={cn(cardLayout, gridTemplateRows)}>
        <Skeleton
          className="w-[310px] h-[232px] from-[oklch(75%_0.17_225/0.12)] via-[oklch(61%_0.27_301/0.08)] to-[oklch(75%_0.17_225/0.12)]"
          animate="wave"
        />
        <div className={centerSectionHeader}>
          <div className="space-y-2">
            <Skeleton className="w-20 h-4" />
            <Skeleton className="w-[350px] h-7" />
            <Skeleton className="w-[320px] h-7" />
            <div className="pt-3 flex flex-row gap-1.5">
              <Skeleton className="w-4 h-4 rounded-xs" />
              <Skeleton className="w-20 h-4 bg-blue-50" />
            </div>
            <div className="flex flex-row gap-1.5">
              <Skeleton className="w-4 h-4 rounded-xs" />
              <Skeleton className="w-24 h-4" />
            </div>
            <div className="pt-2 flex flex-row gap-1.5">
              <Skeleton className="w-30 h-4" />
              <Separator orientation="vertical" />
              <Skeleton className="w-30 h-4" />
              <Separator orientation="vertical" />
              <Skeleton className="w-20 h-4" />
            </div>
            <div className="pt-2 flex flex-row gap-1">
              <Skeleton className="w-16 h-6" />
              <Separator orientation="vertical" />
              <Skeleton className="w-16 h-6" />
              <Separator orientation="vertical" />
              <Skeleton className="w-16 h-6" />
            </div>
          </div>
        </div>
        <Separator
          orientation="vertical"
          className="mt-5 max-h-40 bg-gray-100"
        />
        <div className="flex flex-col pt-8 gap-2">
          <Skeleton className="w-20 h-4" />
          <Skeleton className="w-14 h-6 bg-violet-100" />
          <Separator orientation="horizontal" className="my-2 bg-gray-100" />
          <div className="flex gap-1">
            <Skeleton className="w-16 h-4" />
            <Skeleton className="w-4 h-4 bg-violet-100" />
          </div>
          <Skeleton className="w-20 h-6" />
        </div>
        <div className="flex flex-col gap-2 col-span-4 pr-6 pb-4">
          <Skeleton className="w-20 h-4" />
          <Skeleton className="w-[800px] h-3" />
          <Skeleton className="w-full h-3" />
          <Skeleton className="w-3/4 h-3" />
          <Separator orientation="horizontal" className="my-2 bg-gray-100" />
          <Skeleton className="w-20 h-4" />
          <Skeleton className="w-full h-3" />
        </div>
      </Card>
    );
  }

  const badgeRow = (
    <ArticleBadgeRow
      prominence={article.prominence}
      isLede={article.isLede}
      contentCategory={article.contentCategory}
      publisherTier={article.publisherTier}
      className="shrink-0"
    />
  );

  return (
    <Card
      className={cn(
        cardLayout,
        isSelected && "border border-violet-800 transition-colors duration-150",
        isCardFocused && "ring-2 ring-violet-800 bg-violet-50/30 shadow-lg",
      )}
      style={{
        gridTemplateRows,
      }}
      ref={cardRef}
      tabIndex={0}
      role="article"
      aria-selected={isSelected}
      aria-expanded={isCardFocused}
      aria-label={`${article.headline || "Article"} ${
        isCardFocused
          ? "navigation mode active, press Escape to exit"
          : "press Enter to focus card actions"
      }`}
      aria-describedby={
        isCardFocused ? "card-navigation-instructions" : undefined
      }
      aria-roledescription={
        isCardFocused ? "Card with focus trap navigation" : undefined
      }
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onKeyDown={handleCardKeyDown}
    >
      {isCardFocused && (
        <div id="card-navigation-instructions" className="sr-only">
          You are now in card navigation mode. Use tab to cycle through elements
          within this card. Press Escape to exit.
        </div>
      )}
      <ArticleImage
        size="medium"
        image={image}
        imageUrl={article.articleImage || PlaceHolderImage}
        isHovering={isHovered}
        alt={article.headline || "Article image"}
      />
      <div className={centerSectionHeader}>
        <div className="space-y-0.5">
          <ArticleHeader
            headline={article.headline}
            headlineTranslation={null}
            publisher={article.publisher}
            publisherCountry={article.publisherCountry}
            publisherLogo={article.publisherLogo}
            datePublished={article.datePublished}
            url={article.url}
          />
          <ArticleAuthor authors={article.authors} />
        </div>

        <ScoreDetail
          publisherDomainAuthority={article.publisherDomainAuthority}
          socialEngagement={article.socialEngagement}
          publisherReadership={article.publisherReadership}
          socialMetrics={article.socialMetrics}
        />
        {enableArticleTagging ? (
          <ArticleTagRow
            tags={article.articleTags ?? []}
            className="py-2.5"
            badgeRow={badgeRow}
          />
        ) : (
          badgeRow
        )}
      </div>
      <Separator orientation="vertical" className="mt-5 max-h-40" />
      <ScoreDisplay
        impactScore={article.impactScore}
        sentimentPolarity={article.sentimentPolarity}
        sentimentRationale={article.sentimentRationale}
        sentimentPolarityOriginal={article.sentimentPolarityOriginal}
      />
      {hasSecondSection(article) && (
        <div className="w-full flex flex-col space-y-3 col-span-4 text-xs tracking-wide leading-normal justify-center text-gray-500">
          <ArticleSummary summary={article.summary} />
          {(article.searchMatch || article.firstMention) && (
            <Separator className="mt-4" />
          )}
          {article.firstMention && (
            <div className="gap-2 flex flex-col pb-2">
              <b className="uppercase leading-4">First Mention</b>
              <ArticleMentionText
                term={article.firstMention.term ?? ""}
                snippet={article.firstMention.snippet ?? ""}
              />
            </div>
          )}
          {article.searchMatch && (
            <div className="gap-2 flex flex-col">
              <b className="uppercase leading-4">Search Hit</b>
              <ArticleMentionText searchMatch={article.searchMatch} />
            </div>
          )}
        </div>
      )}
      <div className="flex flex-1 flex-row gap-8 absolute right-4 top-2 items-end">
        {getTranslationLabel(article.language as string | undefined)}
        <MenuTrigger>
          <IconButton
            ref={menuTriggerRef}
            icon={<DotsThreeIcon size={20} />}
            aria-label={"More options"}
            variant={"ghost"}
          />
          <MenuPopover placement="bottom" offset={0} className="w-[216px]">
            <Menu>
              {!isPreview &&
                menuItems.map((item, index) => (
                  <Fragment key={item.id}>
                    <CustomMenuItem config={item} />
                    {index === menuItems.length - 2 && (
                      <MenuSeparator className="mx-1" />
                    )}
                  </Fragment>
                ))}
            </Menu>
          </MenuPopover>
        </MenuTrigger>
      </div>
    </Card>
  );
};

export default ArticleCard;
